import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Checkbox,
  FormControlLabel,
  IconButton,
  Grid,
  Box,
  Typography,
  Chip,
  Autocomplete,
  Alert
} from '@mui/material';
import { Delete as DeleteIcon, Add as AddIcon } from '@mui/icons-material';
import { FormSection, FormActions } from './ui';
import { useFormValidation } from '../hooks/useFormValidation';
import PhotoUploadSection from './PhotoUploadSection';
import api from '../utils/api';

const ClassTypeForm = ({ classType, categories, difficulties, onSubmit, onCancel }) => {
  const { t } = useTranslation();

  const initialData = {
    name: '',
    description: '',
    duration: 60,
    requiredEquipment: [],
    maxCapacity: 20,
    difficulty: 'all-levels',
    category: '',
    isActive: true
  };

  const [photos, setPhotos] = useState([]);
  const [coverPhotoId, setCoverPhotoId] = useState(null);
  const [newEquipment, setNewEquipment] = useState('');

  const validationRules = {
    name: [
      (value) => !value?.trim() ? t('classType:validation.nameRequired') : null,
      (value) => value?.length > 100 ? t('classType:validation.nameTooLong') : null
    ],
    description: [
      (value) => !value?.trim() ? t('classType:validation.descriptionRequired') : null,
      (value) => value?.length > 1000 ? t('classType:validation.descriptionTooLong') : null
    ],
    category: [
      (value) => !value ? t('classType:validation.categoryRequired') : null
    ],
    duration: [
      (value) => {
        const duration = parseInt(value);
        if (isNaN(duration) || duration < 15 || duration > 240) {
          return t('classType:validation.durationRange');
        }
        return null;
      }
    ],
    maxCapacity: [
      (value) => {
        const capacity = parseInt(value);
        if (isNaN(capacity) || capacity < 1 || capacity > 100) {
          return t('classType:validation.capacityRange');
        }
        return null;
      }
    ]
  };

  const {
    data: formData,
    isSubmitting,
    updateField,
    updateFields,
    handleSubmit,
    setFormData,
    getFieldError,
    hasFieldError
  } = useFormValidation(initialData, validationRules);

  // Common equipment options for autocomplete
  const commonEquipment = [
    t('classType:equipment.dumbbells'),
    t('classType:equipment.yogaMat'),
    t('classType:equipment.resistanceBands'),
    t('classType:equipment.kettlebells'),
    t('classType:equipment.barbell'),
    t('classType:equipment.medicineBall'),
    t('classType:equipment.foamRoller'),
    t('classType:equipment.exerciseBall'),
    t('classType:equipment.jumpRope'),
    t('classType:equipment.bosuBall'),
    t('classType:equipment.trxStraps'),
    t('classType:equipment.pullUpBar'),
    t('classType:equipment.bench'),
    t('classType:equipment.stepPlatform'),
    t('classType:equipment.ankleWeights'),
    t('classType:equipment.towel'),
    t('classType:equipment.waterBottle'),
    t('classType:equipment.heartRateMonitor')
  ];

  useEffect(() => {
    if (classType) {
      setFormData({
        name: classType.name || '',
        description: classType.description || '',
        duration: classType.duration || 60,
        requiredEquipment: classType.requiredEquipment || [],
        maxCapacity: classType.maxCapacity || 20,
        difficulty: classType.difficulty || 'all-levels',
        category: classType.category || '',
        isActive: classType.isActive !== undefined ? classType.isActive : true
      });

      // Load class type photos if editing existing class type
      if (classType._id) {
        loadClassTypePhotos(classType._id);
      }
    }
  }, [classType, setFormData]);

  const loadClassTypePhotos = async (classTypeId) => {
    try {
      const response = await api.get(`/class-types/${classTypeId}/photos`);
      if (response.data.success) {
        setPhotos(response.data.data.photos || []);
        setCoverPhotoId(response.data.data.coverPhoto?._id || null);
      }
    } catch (error) {
      console.error('Error loading class type photos:', error);
    }
  };

  const handlePhotoUpload = async (formData) => {
    if (!classType?._id) {
      alert('Please save the class type first before uploading photos');
      return;
    }

    try {
      const response = await api.post(`/class-types/${classType._id}/photos`, formData);

      if (response.data.success) {
        // Reload photos to get updated list
        await loadClassTypePhotos(classType._id);
      }
    } catch (error) {
      console.error('Error uploading photos:', error);
      throw error;
    }
  };

  const handlePhotoDelete = async (photoId) => {
    if (!classType?._id) return;

    try {
      await api.delete(`/class-types/${classType._id}/photos/${photoId}`);
      // Reload photos to get updated list
      await loadClassTypePhotos(classType._id);
    } catch (error) {
      console.error('Error deleting photo:', error);
    }
  };

  const handleSetCoverPhoto = async (photoId) => {
    if (!classType?._id) return;

    try {
      await api.put(`/class-types/${classType._id}/cover-photo`, {
        photoId
      });
      setCoverPhotoId(photoId);
    } catch (error) {
      console.error('Error setting cover photo:', error);
    }
  };

  const handleAddEquipment = () => {
    if (newEquipment.trim() && !formData.requiredEquipment.includes(newEquipment.trim())) {
      updateFields({
        requiredEquipment: [...formData.requiredEquipment, newEquipment.trim()]
      });
      setNewEquipment('');
    }
  };

  const handleRemoveEquipment = (equipmentToRemove) => {
    updateFields({
      requiredEquipment: formData.requiredEquipment.filter(eq => eq !== equipmentToRemove)
    });
  };

  const handleEquipmentKeyDown = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      handleAddEquipment();
    }
  };

  const formatDuration = (minutes) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    
    if (hours === 0) {
      return `${mins} minutes`;
    } else if (mins === 0) {
      return `${hours} hour${hours > 1 ? 's' : ''}`;
    } else {
      return `${hours} hour${hours > 1 ? 's' : ''} ${mins} minutes`;
    }
  };

  const onFormSubmit = async (data) => {
    await onSubmit(data);
  };

  return (
    <Box component="form" onSubmit={handleSubmit(onFormSubmit)} sx={{ maxWidth: 800, mx: 'auto', p: 2 }}>
      {/* Basic Information Section */}
      <FormSection
        title={t('classType:labels.basicInformation')}
        description={t('classType:placeholders.enterBasicDetails')}
        variant="card"
      >
        <Grid container spacing={3} sx={{ width: '100%' }}>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              fullWidth
              label={t('classType:labels.name')}
              required
              value={formData.name}
              onChange={(e) => updateField('name', e.target.value)}
              placeholder={t('classType:placeholders.enterName')}
              error={hasFieldError('name')}
              helperText={getFieldError('name')}
            />
          </Grid>

          <Grid size={{ xs: 12, sm: 6 }}>
            <FormControl fullWidth required>
              <InputLabel>{t('classType:labels.category')}</InputLabel>
              <Select
                value={formData.category}
                onChange={(e) => updateField('category', e.target.value)}
                label={t('classType:labels.category')}
                error={hasFieldError('category')}
              >
                <MenuItem value="">
                  <em>{t('classType:placeholders.selectCategory')}</em>
                </MenuItem>
                {categories?.map((category) => (
                  <MenuItem key={category.value} value={category.value}>
                    {category.label}
                  </MenuItem>
                ))}
              </Select>
              {hasFieldError('category') && (
                <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 2 }}>
                  {getFieldError('category')}
                </Typography>
              )}
            </FormControl>
          </Grid>

          <Grid size={12}>
            <TextField
              fullWidth
              multiline
              rows={3}
              label={t('classType:labels.description')}
              required
              value={formData.description}
              onChange={(e) => updateField('description', e.target.value)}
              placeholder={t('classType:placeholders.enterDescription')}
              error={hasFieldError('description')}
              helperText={getFieldError('description')}
            />
          </Grid>

          <Grid size={{ xs: 12, sm: 4 }}>
            <FormControl fullWidth>
              <InputLabel>{t('classType:labels.difficulty')}</InputLabel>
              <Select
                value={formData.difficulty}
                onChange={(e) => updateField('difficulty', e.target.value)}
                label={t('classType:labels.difficulty')}
              >
                {difficulties?.map((difficulty) => (
                  <MenuItem key={difficulty.value} value={difficulty.value}>
                    {difficulty.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid size={{ xs: 12, sm: 4 }}>
            <TextField
              fullWidth
              type="number"
              label={t('classType:labels.duration')}
              required
              value={formData.duration}
              onChange={(e) => updateField('duration', parseInt(e.target.value))}
              placeholder="60"
              error={hasFieldError('duration')}
              helperText={getFieldError('duration') || formatDuration(formData.duration)}
              inputProps={{ min: 15, max: 240 }}
            />
          </Grid>

          <Grid size={{ xs: 12, sm: 4 }}>
            <TextField
              fullWidth
              type="number"
              label={t('classType:labels.maxCapacity')}
              required
              value={formData.maxCapacity}
              onChange={(e) => updateField('maxCapacity', parseInt(e.target.value))}
              placeholder="20"
              error={hasFieldError('maxCapacity')}
              helperText={getFieldError('maxCapacity') || t('classType:labels.maxParticipants')}
              inputProps={{ min: 1, max: 100 }}
            />
          </Grid>
        </Grid>
      </FormSection>

      {/* Required Equipment Section */}
      <FormSection
        title={t('classType:labels.equipment')}
        description={t('classType:placeholders.specifyEquipment')}
        variant="card"
        collapsible
        defaultExpanded={true}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
            <Autocomplete
              freeSolo
              options={commonEquipment}
              value={newEquipment}
              onInputChange={(event, newInputValue) => {
                setNewEquipment(newInputValue);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t('classType:buttons.addEquipment')}
                  placeholder={t('classType:placeholders.searchEquipment')}
                  onKeyDown={handleEquipmentKeyDown}
                  sx={{ flexGrow: 1 }}
                />
              )}
            />
            <IconButton
              onClick={handleAddEquipment}
              disabled={!newEquipment.trim()}
              color="primary"
              sx={{ 
                mt: 1,
                border: '1px solid',
                borderColor: 'primary.main',
                '&:hover': {
                  backgroundColor: 'primary.light',
                  color: 'primary.contrastText'
                }
              }}
            >
              <AddIcon />
            </IconButton>
          </Box>
          
          {formData.requiredEquipment.length > 0 && (
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Required Equipment:
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {formData.requiredEquipment.map((equipment, index) => (
                  <Chip
                    key={index}
                    label={equipment}
                    onDelete={() => handleRemoveEquipment(equipment)}
                    deleteIcon={<DeleteIcon />}
                    color="primary"
                    variant="outlined"
                  />
                ))}
              </Box>
            </Box>
          )}

          {formData.requiredEquipment.length === 0 && (
            <Alert severity="info" sx={{ mt: 1 }}>
              No equipment required for this class type.
            </Alert>
          )}
        </Box>
      </FormSection>

      {/* Status Section */}
      <FormSection 
        title="Status" 
        description="Set the availability status for this class type"
        variant="card"
      >
        <Box>
          <FormControlLabel
            control={
              <Checkbox
                checked={formData.isActive}
                onChange={(e) => updateField('isActive', e.target.checked)}
                color="primary"
              />
            }
            label={t('classType:labels.active')}
          />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {formData.isActive
              ? t('classType:messages.activeDescription')
              : t('classType:messages.inactiveDescription')
            }
          </Typography>
        </Box>
      </FormSection>

      {/* Photos Section */}
      {classType?._id && (
        <FormSection
          title={t('classType:labels.photos')}
          description={t('classType:placeholders.uploadPhotos')}
          variant="card"
          collapsible
          defaultExpanded={true}
        >
          <PhotoUploadSection
            photos={photos}
            coverPhotoId={coverPhotoId}
            onUpload={handlePhotoUpload}
            onDelete={handlePhotoDelete}
            onSetCover={handleSetCoverPhoto}
            title="Class Type Photos"
            description="Upload photos to showcase this class type and help members understand what to expect"
            maxFiles={15}
          />
        </FormSection>
      )}

      {/* Form Actions */}
      <FormActions
        primaryAction={{
          label: classType ? t('classType:title.edit') : t('classType:title.create'),
          loading: isSubmitting,
          disabled: isSubmitting
        }}
        secondaryAction={{
          label: t('classType:buttons.cancel'),
          onClick: onCancel,
          disabled: isSubmitting
        }}
        alignment="right"
        variant="sticky"
      />
    </Box>
  );
};

export default ClassTypeForm;